/** Shared Tailwind class strings — single source for form controls and primary (brand orange #FF9800) chrome. */

/**
 * Applied once in AppShell around `main` children so every route gets the same content column width.
 * (Avoid putting `mx-auto` + `max-w-*` on each page: as a flex child that can shrink-wrap to intrinsic width.)
 */
export const appMainContentClass = 'mx-auto w-full min-w-0 max-w-[1320px]'

/** AppShell: routes that use the full `main` column width (no `max-w-7xl` cap), e.g. ticket detail. */
export const appMainContentFullWidthClass = 'w-full min-w-0'

/** Standard page padding inside the app content column (dashboard, todos, tickets, jobs). */
export const pageShellClass = 'p-4 sm:p-8 min-w-0'

/** Ticket detail: full main width + horizontal padding (max width is not capped at `7xl`). */
export const pageFullWidthShellClass =
  'flex min-h-0 w-full min-w-0 max-w-none flex-col overflow-hidden px-2 sm:px-3 pb-2 sm:pb-3 h-screen'

/** All Tickets / All Jobs tables — shared chrome and cell rhythm. */
export const dataTableCardClass =
  'w-full min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm'
export const dataTableMinWidthClass = 'min-w-[720px]'
export const dataTableScrollClass = 'w-full min-w-0 overflow-x-auto'

export const dataTableClass = `w-full ${dataTableMinWidthClass} table-fixed border-collapse text-sm`
export const dataTableHeaderRowClass =
  'text-left text-xs text-gray-600 uppercase tracking-wider border-b border-gray-200 bg-white'
/** `min-h` + `align-top`: matches All Tickets (two-line County/City label); All Jobs uses same row height. */
export const dataTableThBase = 'px-6 py-4 font-semibold text-[13px]'
export const dataTableThFilterBase = 'px-4 py-4 text-left font-normal align-middle'
export const dataTableTdBase = 'px-6 py-3.5 align-middle min-w-0'
export const dataTableFilterRowClass = 'border-b border-gray-100 bg-gray-50'
export const dataTableBodyClass = 'divide-y divide-gray-100'

/** Text inputs and textareas (compact, table/toolbar density). */
export const inputClass =
  'w-full min-w-0 text-xs text-gray-700 px-3 py-1.5 border border-gray-200 rounded-lg bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-orange focus:border-brand-orange transition-colors'

/** Login and larger forms — slightly taller controls, ring-2 for focus visibility. */
export const inputClassComfortable =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand-orange focus:border-brand-orange transition-colors'

export const filterInputClass = inputClass

/** Native-style select / filter trigger buttons. */
export const selectTriggerClass =
  'w-full flex items-center justify-between gap-1 text-xs text-gray-700 px-3 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand-orange focus:border-brand-orange transition-colors hover:bg-gray-50'

export const dropdownSelectedRowClass = 'font-medium text-brand-orange-ink'
export const dropdownRowClass = 'text-gray-700'

export const linkTableClass = 'text-xs text-brand-orange hover:text-brand-orange-hover'

/** Solid primary CTA (matches “New Ticket” — #FF9800). */
export const primaryButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium text-white bg-brand-orange border border-brand-orange hover:bg-brand-orange-hover hover:border-brand-orange-hover disabled:opacity-50 disabled:pointer-events-none transition-colors'

export const primaryButtonClassCompact = `${primaryButtonClass} px-4 py-2 shrink-0`

/** Login full-width submit. */
export const primaryButtonClassBlock =
  'w-full bg-brand-orange hover:bg-brand-orange-hover disabled:bg-brand-orange-disabled text-white text-sm font-semibold py-2.5 rounded-lg transition-colors'

/** Modal footer primary (CreateTicket / CreateJob). */
export const primaryButtonModalFooter =
  'px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-brand-orange-hover disabled:opacity-50 transition-colors'

/** Map layer toggle — active state. */
export const primaryToggleActiveClass = 'bg-brand-orange text-white border-brand-orange'

/** Toolbar / header primary actions (ticket detail, etc.). */
export const primaryButtonToolbarClass =
  'flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-brand-orange-hover disabled:opacity-40 disabled:cursor-not-allowed'

/** Toolbar secondary/neutral actions (Waiting on Crew, Update Status, etc.). */
export const secondaryButtonToolbarClass =
  'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'

/** Toolbar ghost cancel — low visual weight, used for Cancel ticket (red text) and Cancel submission step (gray text). */
export const cancelTicketButtonToolbarClass =
  'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed'

export const cancelGhostButtonToolbarClass =
  'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed'

/** Full-width compact primary (e.g. View history). */
export const primaryButtonBlockSmClass =
  'w-full px-3 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-brand-orange-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors'

/** Notification bell dropdown panel — matches sidebar navy palette. */
export const notificationPanelClass =
  'w-80 rounded-xl bg-brand-navy border border-white/[0.12] shadow-[0_20px_40px_rgba(0,0,0,0.45)] overflow-hidden'

export const notificationPanelHeaderClass =
  'flex items-center justify-between px-4 py-3 border-b border-white/10'

export const notificationPanelBodyClass =
  'max-h-[28rem] overflow-y-auto'

export function filterPillClass(active: boolean): string {
  return `px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
    active
      ? 'bg-brand-orange text-white border-brand-orange'
      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
  }`
}
